import type {UserMediaVOResponse} from '@Libraries/user-media-library';
import {MultiMediaUploadSource, UPLOAD_LOADING_KEY, uploadFile, UserMediaType} from '@Libraries/user-media-library';
import {RENDERER_POLL_SLEEP_CADENCE_FORM_FIELD, RendererPollSleepCadence} from '@Libraries/renderer-poll-sleep-cadence';
import type {UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {convertUserMediaVOToAttachment, MAX_ATTACHMENTS, validatePresetsInBackground} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import type {FileUploadPipelineConfig, FileUploadPipelineState} from '@Components/ai-prompt-box/libraries/ai-prompt-box-file-upload.library';
import {handleFilesUpload, retryFileUpload} from '@Components/ai-prompt-box/libraries/ai-prompt-box-file-upload.library';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES = ACCEPTED_IMAGE_TYPES;

export interface DragDropUploadState extends FileUploadPipelineState {
  removeImageAttachments: (hashedFilenames: string[]) => void;
}

const uploadAiChatImageFile = async (file: File): Promise<UserMediaVOResponse> => {
  const fd = new FormData();
  fd.append('Filedata', file);
  fd.append('type', UserMediaType.IMAGE);
  fd.append('mirror', 'false');
  fd.append(RENDERER_POLL_SLEEP_CADENCE_FORM_FIELD, RendererPollSleepCadence.FAST);
  return (await uploadFile(fd, UPLOAD_LOADING_KEY, UserMediaType.IMAGE, MultiMediaUploadSource.UPLOAD)) as UserMediaVOResponse;
};

const buildDragDropUploadConfig = (state: DragDropUploadState, onInvalidFileTypeError?: VoidFunction): FileUploadPipelineConfig<UserMediaVOResponse> => {
  return {
    maxAttachments: MAX_ATTACHMENTS,
    attachmentType: PromptAttachmentType.IMAGE,
    acceptedFileTypes: ACCEPTED_IMAGE_TYPES,
    onUnsupportedFiles: onInvalidFileTypeError,
    uploadFile: uploadAiChatImageFile,
    resolveAttachment: (response: UserMediaVOResponse): UserImageAttachment => convertUserMediaVOToAttachment(response, PromptAttachmentType.IMAGE),
    onAttachmentResolved: (resolved: UserImageAttachment): void => {
      if (resolved.hashedFilename) {
        void validatePresetsInBackground([resolved.hashedFilename], state.removeImageAttachments);
      }
    },
  };
};

export const handleDragDropFilesUpload = (files: File[], state: DragDropUploadState, onInvalidFileTypeError?: VoidFunction): void => {
  handleFilesUpload(files, state, buildDragDropUploadConfig(state, onInvalidFileTypeError));
};

export const retryPendingUpload = (localId: string, state: DragDropUploadState): void => {
  retryFileUpload(localId, state, buildDragDropUploadConfig(state));
};
